import { Handle, Position } from "@xyflow/react";
import type { ApiEndpoint, HttpVerb, RiskSeverity } from "@/lib/arch-parser";
import { cn } from "@/lib/utils";

export interface EndpointNodeData {
  endpoint: ApiEndpoint;
  faded?: boolean;
  highlighted?: boolean;
  [key: string]: unknown;
}

// Verb chips and severity dots map to the status tokens (semantic: GET=safe
// read, POST=create, PUT/PATCH=modify, DELETE=destructive) so they swap with
// the theme — no hardcoded color scales, no dark: overrides (spec §1.8).
const VERB_CHIP: Record<HttpVerb, string> = {
  GET: "bg-success-soft text-success-ink border-success/30",
  POST: "bg-info-soft text-info-ink border-info/30",
  PUT: "bg-warning-soft text-warning-ink border-warning/30",
  PATCH: "bg-warning-soft text-warning-ink border-warning/30",
  DELETE: "bg-danger-soft text-danger-ink border-danger/30",
  HEAD: "bg-surface-tint text-ink-subtle border-border",
  OPTIONS: "bg-surface-tint text-ink-subtle border-border",
};

const SEVERITY_DOT: Record<RiskSeverity, string> = {
  info: "bg-ink-subtle",
  warning: "bg-warning",
  critical: "bg-danger",
};

export function EndpointNode({ data, selected }: { data: EndpointNodeData; selected?: boolean }) {
  const e = data.endpoint;
  const severity: RiskSeverity = e.severity ?? "info";
  return (
    <div
      className={cn(
        "relative rounded-md border-[1.5px] px-2.5 py-1.5 shadow-sm transition-all min-w-[200px] max-w-[260px]",
        "bg-[var(--node-other-fill)] border-[var(--node-other-border)] text-[var(--node-other-ink)]",
        "opacity-90",
        selected &&
          "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)] shadow-md opacity-100 z-10",
        data.faded && "opacity-30",
        data.highlighted && "ring-2 ring-teal-400/70 opacity-100",
      )}
    >
      <Handle type="target" position={Position.Top} id="top" className="!bg-foreground/30" />
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            "text-[9px] font-bold font-mono px-1 py-0.5 rounded border",
            VERB_CHIP[e.verb],
          )}
        >
          {e.verb}
        </span>
        <span
          className={cn("h-1.5 w-1.5 rounded-full shrink-0", SEVERITY_DOT[severity])}
          title={`severity: ${severity}`}
        />
      </div>
      <div
        className="font-mono leading-snug tracking-tight break-all mt-1"
        style={{ fontSize: "calc(10px * var(--archviz-label-scale, 1))" }}
      >
        {e.url_pattern}
      </div>
      {e.what && (
        <div className="text-[9px] text-muted-foreground leading-snug mt-0.5">{e.what}</div>
      )}
      {e.called_by.length > 0 && (
        <div className="text-[9px] text-ink-subtle mt-0.5 font-mono">{e.called_by.join(", ")}</div>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-src"
        className="!bg-foreground/30"
      />
    </div>
  );
}
